import { AuthorizationService } from "../authorization/authorization";
import { LoggingService } from "../loggingSvc";
import { LocalizationService } from "../../../ShellCommon/localization/localization";
import { Prerequisite, PrerequisiteLoadingService, PrerequisiteContext } from "../loading/prerequisite-loading-service";
import { FrameworkFlightingService, FlightingService } from "../flightingSvc";
import { SHA256 } from "crypto-js";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

export interface NavbarRegistrationService {
  registerConfiguration: (configuration: NavbarConfiguration) => void;
}
export interface NavbarConfiguration {
  /**
   * Clickable nav bar items aligned to the right side of nav bar.
   */
  navItemsRight?: NavbarButton[];

  /**
   * Clickable nav bar items aligned to the left side of nav bar.
   */
  navItemsLeft?: NavbarTab[];
  /**
   * Top Left Brand Text/Link
   */
  headerBrand?: NavbarButton;

  /**
   * Return true/false wheather any of this configuration should show. If true or shouldShow not defined, localization files will load
   * that are required by this configuration. RouteName is the route being loaded name.
   *
   * Note: This will be executed on every state change, so if your method is time consuming, it's better to memoize.
   */
  shouldShow?: (routeName: string) => boolean;

  /**
   * The localization App Id for the configuration.
   */
  locApp: string;
}
export interface NavbarMenuItem extends NavItem {
  /**
   * Menu Item was pressed or interacted with via ui. The event will be a KeyboardEvent if the keyboard was used to press the button.
   */
  onActivated?: ($event: ng.IAngularEvent | KeyboardEvent) => void;
}
export interface NavbarButton extends NavItem {
  /**
   * Button was pressed or interacted with via ui. The event will be a KeyboardEvent if a keyboard shortcut was used.
   */
  onActivated?: ($event: ng.IAngularEvent | KeyboardEvent) => void;
  /**
   * Alt + this key will simulate a click on the button.
   */
  keyboardShortcut?: string;
  /**
   * Optional Glyph item to be displayed next to navItem.
   */
  glyph?: NavItemGlyph;
}

export interface NavbarTab extends NavItem {
  /**
   * The ui route that the page will navigate to once the tab has been clicked/activated.
   */
  uiRef: string;

  /**
   * Return true/false whether this tab should be decorated as the active tab.
   * The current state's name will be provided to the function as a convenience.
   */
  isActive?: (state: angular.ui.IState) => boolean;
}
export interface NavItem {
  /**
   * Localization key for text that should be displayed for this nav item.
   */
  locKey?: string;

  /**
   * A number between 0 and 100. Items with smaller order will be on the left or top. Headerbrand order allows lower orders to override higher orders.
   * Orders [11-19] and [81-89] are reserved for the SDK.
   */
  order?: number;
  /**
   * Flight to check whether to display this item.
   */
  flightName?: string;
  /**
   * Auth right to check whether to display this item.
   */
  authRight?: string;

  /**
   * Auth application ID. Used in conjunction with authRight.
   */
  authApplicationId?: number;
  /**
   * Optional callback function to determine if should show element. Return true to show.
   */
  shouldShow?: () => boolean;

  /**
   * Automation ID. For use in selectors in E2E tests.
   */
  automationId?: string;
}
export interface NavItemGlyph {
  /**
   * The class of the glyph to add to the nav item.
   */
  glyphClass: string;
  /**
   * Either 'LEFT' or 'RIGHT', depending on which side of the nav item you'd like the glyph to show.
   */
  glyphPosition: GlyphPosition;
}
/**
 * Position of the glyph relative to the nav item. Either 'LEFT' or 'RIGHT'.
 */
export type GlyphPosition = "LEFT" | "RIGHT";

export interface OrderableNavItem {
  order: string;
}
export interface NavItemInternal extends OrderableNavItem {
  onActivated?: ($event: ng.IAngularEvent | KeyboardEvent) => void;
  localizedText: string;
  glyph?: NavItemGlyph;
  uiRef?: string;
  automationId: string;
  isActive?: boolean;
}
export class NavbarRegistrationServiceImplementation implements NavbarRegistrationService {
  public navItemsRight: NavItemInternal[] = [];
  public navItemsLeft: NavItemInternal[] = [];
  private stateChangeMethods: any[] = [];
  public mapKeyboardShortcuts: Map<string, ($event: ng.IAngularEvent | KeyboardEvent) => void>;
  private configurations: NavbarConfiguration[] = [];
  public headerBrand: NavItemInternal;
  private shouldShowMap = new Map<NavbarConfiguration, boolean>();
  private loadingDeferral: ng.IDeferred<void>;

  // @ngInject
  constructor(
    private sdLocalizationService: LocalizationService,
    // We use the flighting service's async method during prereq load and sync methods at all other times.
    private flightingService: FlightingService & FrameworkFlightingService,
    private authorizationService: AuthorizationService,
    private $q: ng.IQService,
    private $rootScope: ng.IRootScopeService,
    private $state: angular.ui.IStateService,
    private loggingService: LoggingService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation
  ) {
    this.loadingDeferral = $q.defer<void>();
    $rootScope.$on("$stateChangeSuccess", this.handleStateChange.bind(this));
  }
  public registerConfiguration(configuration: NavbarConfiguration) {
    this.configurations.push(configuration);
  }

  public handleStateChange() {
    this.stateChangeMethods.forEach((m) => m(this.$state.current));
  }

  public getDefaultUIRef(): ng.IPromise<string> {
    return this.loadingDeferral.promise.then(() => {
      if (!this.navItemsLeft || !this.navItemsLeft.length) {
        return undefined;
      }
      return this.navItemsLeft[0].uiRef;
    });
  }

  public configure() {
    this.headerBrand = undefined;
    this.navItemsLeft = [];
    this.navItemsRight = [];
    this.stateChangeMethods = [];
    this.mapKeyboardShortcuts = new Map<string, ($event: ng.IAngularEvent | KeyboardEvent) => void>();
    this.configurations.forEach((configuration) => {
      if (this.shouldShowMap.get(configuration)) {
        // All components that might require an authenicated user
        if (this.isAuthenticated() && configuration.navItemsRight) {
          configuration.navItemsRight.forEach((item) => {
            if (this.shouldShow(item)) {
              if (item.keyboardShortcut) {
                let activatedFunction = item.onActivated;
                let shortcut = item.keyboardShortcut.toUpperCase();
                if (this.mapKeyboardShortcuts.has(shortcut)) {
                  this.loggingService.logError("Multiple shortcuts mapped to the same key for shortcut: " + shortcut);
                  activatedFunction = () => {
                    let localizedErrorMessage = this.sdLocalizationService.getLocalizedString(
                      "ServiceDesk",
                      "_NAV_BAR_SHORTCUT_BAD_CONFIGURATION_"
                    );
                    this.loggingService.logError(
                      "User tried using a shortcut which was configured incorrectly",
                      undefined,
                      { displayText: localizedErrorMessage, uId: "navbar_shortcut_misconfiguration" }
                    );
                  };
                }
                if (item.keyboardShortcut.length != 1) {
                  throw new Error(`Invalid keyboard shortcut: ${shortcut}. Shortcut must be one character`);
                }
                this.mapKeyboardShortcuts.set(shortcut, activatedFunction);
              }
              let localizedText = item.locKey
                ? this.sdLocalizationService.getLocalizedString(configuration.locApp, item.locKey)
                : "";
              this.navItemsRight.push({
                localizedText: localizedText,
                onActivated: item.onActivated,
                order: this.getOrder(item, configuration.locApp),
                glyph: item.glyph,
                automationId: item.automationId
              });
            }
          });
        }
        if (this.isAuthenticated() && configuration.navItemsLeft) {
          configuration.navItemsLeft.forEach((item) => {
            if (this.shouldShow(item)) {
              let localizedText = this.sdLocalizationService.getLocalizedString(configuration.locApp, item.locKey);
              let navItem: NavItemInternal = {
                localizedText: localizedText,
                uiRef: item.uiRef,
                order: this.getOrder(item, configuration.locApp),
                automationId: item.automationId,
                isActive: false
              };
              this.navItemsLeft.push(navItem);
              if (angular.isFunction(item.isActive)) {
                navItem.isActive = item.isActive(this.$state.current);
                this.stateChangeMethods.push((state: angular.ui.IState) => {
                  this.$rootScope.$applyAsync(() => {
                    navItem.isActive = item.isActive(state);
                  });
                });
              }
            }
          });
        }
        // All components that do NOT require an authenicated user
        if (configuration.headerBrand) {
          let order = this.getOrder(configuration.headerBrand, configuration.locApp);
          if (!this.headerBrand || this.compareOrders(this.headerBrand.order, order) > 0) {
            if (this.shouldShow(configuration.headerBrand)) {
              let localizedText = this.sdLocalizationService.getLocalizedString(
                configuration.locApp,
                configuration.headerBrand.locKey
              );
              this.headerBrand = {
                localizedText: localizedText,
                onActivated: configuration.headerBrand.onActivated,
                order: order,
                glyph: configuration.headerBrand.glyph,
                automationId: configuration.headerBrand.automationId
              };
            }
          }
        }
      }
    });
    this.navItemsRight.sort(this.compareNavItems.bind(this));
    this.navItemsLeft.sort(this.compareNavItems.bind(this));
  }

  private compareNavItems(navItemA: OrderableNavItem, navItemB: OrderableNavItem): number {
    return this.compareOrders(navItemA.order, navItemB.order);
  }

  private compareOrders(orderA: string, orderB: string) {
    if (orderA == orderB) {
      return 0;
    }
    if (orderA.length != orderB.length) {
      return orderA.length - orderB.length;
    }
    return orderA < orderB ? -1 : 1;
  }

  private getOrder(item: NavItem, locApp: string): string {
    if (item.order != undefined) {
      return item.order.toString();
    }
    return SHA256(item.locKey + locApp).toString();
  }

  private shouldShow(item: NavItem): boolean {
    let shouldShow = true;
    if (item.flightName) {
      shouldShow = this.flightingService.isFlightEnabled(item.flightName);
    }
    if (item.authRight) {
      shouldShow = shouldShow && this.authorizationService.hasRight(item.authRight, item.authApplicationId);
    }
    if (item.shouldShow) {
      shouldShow = shouldShow && item.shouldShow();
    }
    return shouldShow;
  }

  public loadPrerequisites(prerequisiteContext: PrerequisiteContext): ng.IPromise<any> {
    let localizationAppIds = new Set<string>();
    this.configurations.forEach((configuration) => {
      let shouldShow = !configuration.shouldShow || configuration.shouldShow(prerequisiteContext.routeName);
      this.shouldShowMap.set(configuration, shouldShow);
      if (shouldShow) {
        localizationAppIds.add(configuration.locApp);
      }
    });
    let promiseList: ng.IPromise<any>[] = [];
    localizationAppIds.forEach((appId: string) => {
      promiseList.push(this.sdLocalizationService.initLocalizedResources(appId));
    });
    return this.$q
      .all(promiseList.concat(this.getSupportingPrereqs()))
      .then(() => this.configure())
      .finally(() => this.loadingDeferral.resolve());
  }

  private getSupportingPrereqs() {
    return this.isAuthenticated()
      ? this.$q.all([this.authorizationService.loadRights(), this.flightingService.getFlights()])
      : this.$q.resolve();
  }

  private isAuthenticated() {
    return this.msalAuthenticationService.userInfo.isAuthenticated;
  }
}
